/**
 * ExplorePage - Discover public profiles in the community
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Users, Globe, Award, Plane, Crown, MapPin, 
  Loader2, ChevronLeft, Search
} from "lucide-react";

const ExplorePage = () => {
  const [profiles, setProfiles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [sortBy, setSortBy] = useState("recent");
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchProfiles(1, true);
  }, [sortBy]);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/profile/explore/stats`);
      setStats(res.data);
    } catch (error) {
      console.error("Failed to fetch community stats");
    }
  };

  const fetchProfiles = async (pageNum, reset = false) => {
    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    
    try {
      const res = await axios.get(`${API}/profile/explore/profiles`, {
        params: { page: pageNum, limit: 20, sort_by: sortBy }
      });
      
      if (reset) {
        setProfiles(res.data.profiles);
      } else {
        setProfiles(prev => [...prev, ...res.data.profiles]);
      }
      setHasMore(res.data.has_more);
      setPage(pageNum);
    } catch (error) {
      console.error("Failed to fetch profiles");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = () => {
    fetchProfiles(page + 1, false);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary to-primary/80 text-white">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4">
            <ChevronLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
          
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-8 h-8" />
            <h1 className="font-serif text-3xl font-bold">Explore Community</h1>
          </div>
          <p className="text-white/80">Discover fellow adventurers and their bucket lists</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Community Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-card border-none shadow-sm">
              <CardContent className="p-4 text-center">
                <Users className="w-6 h-6 mx-auto mb-2 text-primary" />
                <p className="text-2xl font-bold text-charcoal">{stats.total_explorers}</p>
                <p className="text-sm text-muted-foreground">Explorers</p>
              </CardContent>
            </Card>
            <Card className="bg-card border-none shadow-sm">
              <CardContent className="p-4 text-center">
                <Plane className="w-6 h-6 mx-auto mb-2 text-secondary" />
                <p className="text-2xl font-bold text-charcoal">{stats.total_adventures}</p>
                <p className="text-sm text-muted-foreground">Adventures</p>
              </CardContent>
            </Card>
            <Card className="bg-card border-none shadow-sm">
              <CardContent className="p-4 text-center">
                <Globe className="w-6 h-6 mx-auto mb-2 text-accent" />
                <p className="text-2xl font-bold text-charcoal">{stats.countries_explored}</p>
                <p className="text-sm text-muted-foreground">Countries</p>
              </CardContent>
            </Card>
            <Card className="bg-card border-none shadow-sm">
              <CardContent className="p-4 text-center">
                <Crown className="w-6 h-6 mx-auto mb-2 text-amber-500" />
                <p className="text-2xl font-bold text-charcoal">{stats.founding_members}</p>
                <p className="text-sm text-muted-foreground">Founders</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Sort & Filter */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-xl font-medium text-charcoal">
            Public Profiles
          </h2>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[180px]" data-testid="sort-profiles">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">Most Recent</SelectItem>
              <SelectItem value="adventures">Most Adventures</SelectItem>
              <SelectItem value="countries">Most Countries</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Profiles Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : profiles.length === 0 ? (
          <Card className="bg-card border-none shadow-sm">
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-serif text-xl font-medium text-charcoal mb-2">
                No public profiles yet
              </h3>
              <p className="text-muted-foreground mb-4">
                Be the first to make your profile public and inspire others!
              </p>
              <Link to="/profile">
                <Button className="bg-primary text-white">
                  Make Your Profile Public
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {profiles.map((profile) => (
                <Link 
                  key={profile.username} 
                  to={`/u/${profile.username}`}
                  className="block"
                  data-testid={`profile-card-${profile.username}`}
                >
                  <Card className="bg-card border-none shadow-sm hover:shadow-md transition-shadow h-full">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        {/* Avatar */}
                        {profile.profile_picture_path || profile.avatar_url ? (
                          <img 
                            src={profile.profile_picture_path 
                              ? `${API}/profile/picture/${profile.profile_picture_path}`
                              : profile.avatar_url
                            }
                            alt={profile.name}
                            className={`w-14 h-14 rounded-full object-cover flex-shrink-0 ${profile.streak_frame_enabled ? 'streak-flame-frame' : ''}`}
                          />
                        ) : (
                          <div className={`w-14 h-14 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white text-xl font-bold flex-shrink-0 ${profile.streak_frame_enabled ? 'streak-flame-frame' : ''}`}>
                            {profile.name?.charAt(0)?.toUpperCase() || "?"}
                          </div>
                        )}
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-charcoal truncate">
                              {profile.name}
                            </h3>
                            {profile.founding_explorer && (
                              <Crown className="w-4 h-4 text-amber-500 flex-shrink-0" title="Founding Explorer" />
                            )}
                            {profile.streak_legend_title && (
                              <span className="text-xs px-1.5 py-0.5 bg-pink-500/20 text-pink-500 rounded-full flex-shrink-0">Legend</span>
                            )}
                            {profile.ambassador_level && (
                              <span className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                                profile.ambassador_level === "gold" ? "bg-yellow-500/20 text-yellow-600" :
                                profile.ambassador_level === "silver" ? "bg-slate-300/20 text-slate-500" :
                                "bg-amber-500/20 text-amber-600"
                              }`}>{profile.ambassador_level === "gold" ? "Gold" : profile.ambassador_level === "silver" ? "Silver" : ""} Ambassador</span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">@{profile.username}</p>
                          
                          {profile.bio && (
                            <p className="text-sm text-charcoal/70 mt-2 line-clamp-2">
                              {profile.bio}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {/* Stats */}
                      <div className="flex items-center gap-4 mt-4 pt-4 border-t border-charcoal/5">
                        <div className="flex items-center gap-1.5 text-sm">
                          <Plane className="w-4 h-4 text-primary" />
                          <span className="font-medium">{profile.stats?.completed_items || 0}</span>
                          <span className="text-muted-foreground">trips</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-sm">
                          <MapPin className="w-4 h-4 text-secondary" />
                          <span className="font-medium">{profile.stats?.countries_visited || 0}</span>
                          <span className="text-muted-foreground">countries</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-sm">
                          <Award className="w-4 h-4 text-accent" />
                          <span className="font-medium">{profile.stats?.badges || 0}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>

            {/* Load More */}
            {hasMore && (
              <div className="text-center mt-8">
                <Button
                  variant="outline"
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="min-w-[200px]"
                >
                  {loadingMore ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    "Load More Explorers"
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ExplorePage;
